/**
 * Preventivo analitico dell'offerta: il foglio "Riesame Offerta" dentro il
 * gestionale.
 *
 * Tariffe, voci con le quantità ripartite fra fino a tre consulenti, fee e
 * maggiorazione: il prezzo dell'offerta si calcola da solo, e accanto si
 * vedono i costi alle tariffe dei consulenti e il margine. Al cliente, se lo
 * si sceglie, arriva la tabella con i prezzi unitari già comprensivi di fee e
 * maggiorazione — mai il ricarico come voce a parte.
 */
import { useEffect } from 'react';
import CampoImporto from './CampoImporto';
import {
  TIPI_TARIFFA, preventivoVuoto, calcolaPreventivo, perConsulente,
  prezzoUnitario, quantitaRiga, isSezione, righeDellaSezione,
} from './calcoloPreventivo';

/** Questa voce sta sotto una macro voce? Serve solo a rientrarla a video. */
const sottoSezione = (righe, i) => {
  for (let j = i - 1; j >= 0; j--) if (isSezione(righe[j])) return true;
  return false;
};

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
const qta = n => String(Math.round((Number(n) || 0) * 100) / 100).replace('.', ',');
const label = 'block text-xs font-medium text-gray-500 mb-0.5';

export default function PannelloPreventivo({ preventivo, onChange, consulenti = [], onImporto,
                                             importo, onImportoChange }) {
  const attivo = !!preventivo?.attivo;
  const prev = preventivo || preventivoVuoto();
  const calcolo = attivo ? calcolaPreventivo(prev) : null;

  // Il prezzo dell'offerta segue il preventivo: ricopiarlo a mano è proprio
  // l'errore che il preventivo nel gestionale deve evitare.
  useEffect(() => {
    if (attivo && calcolo) onImporto?.(calcolo.importo);
  }, [attivo, calcolo?.importo]);   // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => onChange({ ...prev, [k]: v });
  const setRiga = (i, k, v) => set('righe', prev.righe.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const setQ = (i, col, v) => setRiga(i, 'quantita',
    [0, 1, 2].map(c => c === col ? v : (prev.righe[i].quantita || [])[c] ?? ''));
  const setFee = (i, k, v) => set('fee', prev.fee.map((f, j) => j === i ? { ...f, [k]: v } : f));

  if (!attivo) {
    return (
      <div className="bg-gray-50 border rounded-lg p-3 flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Prezzo</div>
          <p className="text-xs text-gray-500 mt-0.5">
            Importo a corpo indicato a mano. Per calcolarlo da voci, quantità e tariffe usa il preventivo.
          </p>
        </div>
        {/* Il prezzo si scrive qui, dove si decide: nel modulo in alto era
            lontano dal calcolo che dovrebbe produrlo. */}
        {onImportoChange && (
          <div className="w-40">
            <label className={label}>Importo dell'offerta (€)</label>
            <CampoImporto className="input py-1 text-sm" value={importo ?? ''} onChange={onImportoChange} />
          </div>
        )}
        <button type="button" className="btn-secondary btn-sm"
                onClick={() => onChange({ ...(preventivo || preventivoVuoto()), attivo: true })}>
          🧮 Calcola con un preventivo
        </button>
      </div>
    );
  }

  const cons = perConsulente(prev, consulenti);
  const costoTot = cons.reduce((s, c) => s + (c?.costo || 0), 0);
  const margine = calcolo.importo - costoTot;
  // Si mostrano le colonne dei consulenti scelti, e quelle che hanno ancora
  // quantità: nascondere una colonna piena lascerebbe nel totale numeri che
  // non si vedono più.
  const colonneAttive = [0, 1, 2].filter(c =>
    prev.consulenti?.[c] || prev.righe.some(r => String((r.quantita || [])[c] ?? '').trim()));
  const colonne = colonneAttive.length ? colonneAttive : [0];

  return (
    <div className="bg-gray-50 border rounded-lg p-3 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Preventivo</div>
          <p className="text-xs text-gray-500 mt-0.5">
            Il prezzo dell'offerta si calcola da qui. Lascia vuote le voci che non servono.
          </p>
        </div>
        <button type="button" className="btn-secondary btn-sm shrink-0"
                title="Torna a un importo a corpo scritto a mano (il preventivo resta salvato)"
                onClick={() => set('attivo', false)}>Importo a mano</button>
      </div>

      {/* Tariffe e consulenti */}
      <div className="grid grid-cols-3 gap-2">
        {TIPI_TARIFFA.filter(t => t.v !== 'libera').map(t => (
          <div key={t.v}>
            <label className={label}>Tariffa {t.l.toLowerCase()} (€)</label>
            <CampoImporto className="input py-1 text-sm" value={prev.tariffe?.[t.v] ?? ''}
                          onChange={v => set('tariffe', { ...prev.tariffe, [t.v]: v })} />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[0, 1, 2].map(col => (
          <div key={col}>
            <label className={label}>Consulente {col + 1}</label>
            <select className="input py-1 text-sm" value={prev.consulenti?.[col] || ''}
                    onChange={e => set('consulenti', [0, 1, 2].map(c =>
                      c === col ? (e.target.value || null) : (prev.consulenti?.[c] ?? null)))}>
              <option value="">—</option>
              {consulenti.map(c => (
                <option key={c.id} value={c.id}>{c.nome} {c.cognome}{c.interno ? ' (Sinthema)' : ''}</option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {/* Voci */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b">
              <th className="pb-1.5 pr-2">Voce</th>
              <th className="pb-1.5 pr-2 w-32">Tariffa</th>
              {colonne.map(col => (
                <th key={col} className="pb-1.5 pr-2 w-20 text-right">
                  {cons[col]?.nome?.split(' ')[0] || `Cons. ${col + 1}`}
                </th>
              ))}
              <th className="pb-1.5 pr-2 w-16 text-right">Totale</th>
              <th className="pb-1.5 w-24 text-right">Importo</th>
              <th className="pb-1.5 w-6"></th>
            </tr>
          </thead>
          <tbody>
            {prev.righe.map((r, i) => isSezione(r) ? (
              // Macro voce: un titolo e il totale di quello che ci sta sotto.
              // Niente tariffa né quantità — quelle stanno nelle sue voci.
              <tr key={i} className="border-b last:border-0 bg-brand-50/60">
                <td className="py-1 pr-2" colSpan={2 + colonne.length}>
                  <input className="input py-1 text-sm font-semibold" placeholder="Macro voce (es. Adeguamento AI)"
                         value={r.descrizione} onChange={e => setRiga(i, 'descrizione', e.target.value)} />
                </td>
                <td className="py-1.5 pr-2 text-right text-gray-600">
                  {(() => {
                    const g = righeDellaSezione(prev.righe, i)
                      .reduce((s, x) => s + quantitaRiga(x), 0);
                    return g ? qta(g) : '';
                  })()}
                </td>
                <td className="py-1.5 text-right font-semibold text-brand-700">
                  {(() => {
                    const tot = righeDellaSezione(prev.righe, i)
                      .reduce((s, x) => s + quantitaRiga(x) * prezzoUnitario(prev, x), 0);
                    return tot ? fmt(tot) : '';
                  })()}
                </td>
                <td className="py-1.5 text-right">
                  <button type="button" className="text-gray-300 hover:text-red-600" title="Togli la macro voce"
                          onClick={() => set('righe', prev.righe.filter((_, j) => j !== i))}>✕</button>
                </td>
              </tr>
            ) : (
              <tr key={i} className="border-b last:border-0 align-top">
                <td className="py-1 pr-2">
                  <input className={`input py-1 text-sm ${sottoSezione(prev.righe, i) ? 'ml-4 w-[calc(100%-1rem)]' : ''}`}
                         value={r.descrizione}
                         onChange={e => setRiga(i, 'descrizione', e.target.value)} />
                </td>
                <td className="py-1 pr-2">
                  <select className="input py-1 text-sm" value={r.tariffa}
                          onChange={e => setRiga(i, 'tariffa', e.target.value)}>
                    {TIPI_TARIFFA.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
                  </select>
                  {r.tariffa === 'libera' && (
                    <div className="mt-1">
                      <CampoImporto className="input py-1 text-sm" value={r.prezzo ?? ''}
                                    placeholder="prezzo €" onChange={v => setRiga(i, 'prezzo', v)} />
                    </div>
                  )}
                </td>
                {colonne.map(col => (
                  <td key={col} className="py-1 pr-2">
                    <input className="input py-1 text-sm text-right" inputMode="decimal"
                           value={(r.quantita || [])[col] ?? ''} placeholder="0"
                           onChange={e => setQ(i, col, e.target.value)} />
                  </td>
                ))}
                <td className="py-1.5 pr-2 text-right text-gray-600">{quantitaRiga(r) ? qta(quantitaRiga(r)) : ''}</td>
                <td className="py-1.5 text-right font-medium">
                  {quantitaRiga(r) ? fmt(quantitaRiga(r) * prezzoUnitario(prev, r)) : ''}
                </td>
                <td className="py-1.5 text-right">
                  <button type="button" className="text-gray-300 hover:text-red-600" title="Togli la voce"
                          onClick={() => set('righe', prev.righe.filter((_, j) => j !== i))}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Fee, maggiorazione e arrotondamento si ripartiscono sui prezzi
          unitari: la tariffa scritta qui sopra e quella che legge il cliente
          possono non coincidere. Succedeva in silenzio — ora si vede. */}
      {(() => {
        const conQuantita = calcolo.righe.filter(r => r.q > 0);
        const diverse = (calcolo.visibili || [])
          .map((v, i) => ({ v, base: conQuantita[i]?.pu || 0 }))
          .filter(x => x.base > 0 && Math.abs(x.v.unitario - x.base) >= 0.01);
        if (!diverse.length) return null;
        // Una riga per tariffa, non per voce: le voci che condividono la
        // tariffa hanno lo stesso scostamento.
        const viste = new Set();
        const righe = diverse.filter(x => {
          const k = `${x.v.tariffa}-${x.base}`;
          return viste.has(k) ? false : (viste.add(k), true);
        });
        return (
          <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-2.5 py-2 space-y-0.5">
            <div className="font-medium">Nel documento il cliente leggerà prezzi diversi dalle tariffe qui sopra:</div>
            {righe.map((x, i) => (
              <div key={i}>
                {x.v.descrizione}: <strong>{fmt(x.v.unitario)}</strong> invece di {fmt(x.base)}
              </div>
            ))}
            <div className="text-amber-700/80">
              Dipende da fee, maggiorazione e arrotondamento, che si ripartiscono nei prezzi unitari.
              Per far uscire le tariffe come le hai scritte, azzera maggiorazione e fee.
            </div>
          </div>
        );
      })()}

      <div className="flex gap-2 flex-wrap items-center">
        <button type="button" className="btn-secondary btn-sm"
                onClick={() => set('righe', [...prev.righe, { descrizione: '', tariffa: 'giorno', prezzo: '', quantita: ['', '', ''] }])}>
          + Aggiungi voce
        </button>
        {/* Le attività che seguono un titolo gli appartengono, fino al titolo
            dopo: è il modo in cui i preventivi si leggono — "AI: 3 gg" e sotto
            cosa ci sta dentro. */}
        <button type="button" className="btn-secondary btn-sm"
                title="Un titolo che raggruppa le attività che lo seguono"
                onClick={() => set('righe', [...prev.righe, { sezione: true, descrizione: '' }])}>
          + Aggiungi macro voce
        </button>
        <span className="text-xs text-gray-400">
          Le voci sotto una macro voce le appartengono, fino alla macro voce successiva.
        </span>
      </div>

      {/* Fee e maggiorazione: interne, non compaiono come voci al cliente */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
        <div className="md:col-span-2 space-y-1.5">
          <div className={label}>Fee (a nostro uso: si ripartiscono nei prezzi)</div>
          {prev.fee.map((f, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input className="input py-1 text-sm flex-1" value={f.descrizione}
                     onChange={e => setFee(i, 'descrizione', e.target.value)} />
              <div className="w-32">
                <CampoImporto className="input py-1 text-sm" value={f.importo ?? ''}
                              onChange={v => setFee(i, 'importo', v)} />
              </div>
              <button type="button" className="text-gray-300 hover:text-red-600" title="Togli la fee"
                      onClick={() => set('fee', prev.fee.filter((_, j) => j !== i))}>✕</button>
            </div>
          ))}
          <button type="button" className="btn-secondary btn-sm"
                  onClick={() => set('fee', [...prev.fee, { descrizione: '', importo: '' }])}>+ Aggiungi fee</button>
        </div>
        <div className="space-y-2">
          <div>
            <label className={label}>Maggiorazione (%)</label>
            <input className="input py-1 text-sm" inputMode="decimal" value={prev.maggiorazione ?? ''}
                   onChange={e => set('maggiorazione', e.target.value)} />
          </div>
          {/* Lo sconto, a differenza di fee e maggiorazione, il cliente lo
              vede: è una riga del dettaglio. */}
          <div>
            <label className={label}>Sconto al cliente</label>
            <div className="flex gap-1">
              <input className="input py-1 text-sm" inputMode="decimal" placeholder="nessuno"
                     value={prev.sconto?.valore ?? ''}
                     onChange={e => set('sconto', { tipo: prev.sconto?.tipo || 'percentuale', valore: e.target.value })} />
              <div className="w-16 shrink-0">
                <select className="input py-1 text-sm" value={prev.sconto?.tipo || 'percentuale'}
                        onChange={e => set('sconto', { ...(prev.sconto || {}), tipo: e.target.value })}>
                  <option value="percentuale">%</option>
                  <option value="importo">€</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Riepilogo: prezzo al cliente e, solo per noi, costi e margine */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white border rounded-lg p-2.5 text-sm space-y-0.5">
          <div className="flex justify-between"><span className="text-gray-500">Attività</span><span>{fmt(calcolo.subtotale)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Fee</span><span>{fmt(calcolo.totaleFee)}</span></div>
          <div className="flex justify-between">
            <span className="text-gray-500">Maggiorazione {String(prev.maggiorazione || 0).replace('.', ',')}%</span>
            <span>{fmt(calcolo.offerta - calcolo.subtotale - calcolo.totaleFee)}</span>
          </div>
          {calcolo.sconto > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">Sconto (visibile al cliente)</span><span>− {fmt(calcolo.sconto)}</span>
            </div>
          )}
          {calcolo.manuale && Math.abs(calcolo.prezzo - calcolo.calcolato) >= 0.01 && (
            <div className="flex justify-between">
              <span className="text-gray-500">Arrotondamento</span>
              <span>
                {calcolo.prezzo > calcolo.calcolato ? '+ ' : '− '}{fmt(Math.abs(calcolo.prezzo - calcolo.calcolato))}
                <button type="button" className="ml-1.5 text-xs text-brand-600 hover:underline"
                        onClick={() => set('prezzoFinale', '')}>annulla</button>
              </span>
            </div>
          )}
          <div className="flex justify-between border-t pt-1 font-semibold text-brand-700">
            <span>Prezzo dell'offerta</span><span>{fmt(calcolo.importo)}</span>
          </div>
          {/* Il prezzo calcolato finisce qui dentro, dove si può ritoccare:
              scrivendo un altro numero si arrotonda, svuotando si torna al
              calcolo. */}
          {onImportoChange ? (
            <div className="flex items-end justify-between gap-2 pt-1.5">
              <label className="text-[11px] text-gray-500 leading-tight pb-1.5">
                Importo dell'offerta<br />
                <span className="text-gray-400">calcolato, modificabile per arrotondare</span>
              </label>
              <div className="w-32">
                <CampoImporto className="input py-1 text-sm text-right" value={importo ?? ''}
                              onChange={onImportoChange} />
              </div>
            </div>
          ) : (
            <p className="text-[11px] text-gray-400 pt-0.5">
              Per arrotondare scrivi il prezzo finale nel campo Importo dell'offerta.
            </p>
          )}
        </div>
        <div className="bg-white border rounded-lg p-2.5 text-sm space-y-0.5">
          {cons.filter(Boolean).length === 0 ? (
            <p className="text-xs text-gray-400">Scegli i consulenti per vedere costi e margine.</p>
          ) : cons.filter(Boolean).map(c => (
            <div key={c.col} className="flex justify-between gap-2">
              <span className="text-gray-500 truncate">
                {c.nome} · {qta(c.giornate)} gg
              </span>
              <span className={c.interno ? 'text-gray-400' : ''}>
                {c.interno ? 'Sinthema' : c.senzaTariffa ? <span className="text-amber-600" title="Mancano tariffe nel suo profilo">{fmt(c.costo)} ?</span> : fmt(c.costo)}
              </span>
            </div>
          ))}
          {cons.filter(Boolean).length > 0 && (
            <div className={`flex justify-between border-t pt-1 font-semibold ${margine >= 0 ? 'text-green-700' : 'text-red-600'}`}>
              <span>Margine</span>
              <span>{fmt(margine)}{calcolo.importo > 0 ? ` (${Math.round(margine / calcolo.importo * 100)}%)` : ''}</span>
            </div>
          )}
        </div>
      </div>

      <label className="flex items-start gap-2 text-sm text-gray-700">
        <input type="checkbox" className="w-4 h-4 mt-0.5" checked={!!prev.mostraDettaglio}
               onChange={e => set('mostraDettaglio', e.target.checked)} />
        <span>
          Mostra al cliente il dettaglio (voci, quantità e prezzi unitari)
          <span className="block text-xs text-gray-500">
            Fee e maggiorazione non compaiono: sono già dentro i prezzi unitari. Senza spunta
            il cliente vede solo il totale a corpo.
          </span>
        </span>
      </label>
    </div>
  );
}
